import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { dutchPersonNumberValidator } from 'src/app/validators/dutchPersonNumber';
import { dateOfBirthValidator } from 'src/app/validators/dateOfBirth';
import { Subscription, TimeoutError  } from 'rxjs';
import { mergeMap, retry } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { LoginApiService } from 'src/app/services/api/login-api.service';
import { ApplicationService } from 'src/app/services/application-service';
import { TrackingService } from 'src/app/services/tracking.service';
import { FlowService } from 'src/app/services/flow.service';
import { AuthService } from 'src/app/services/auth.service';

import { VerifyWeakResponse } from 'src/app/models/login.model';
import { AddApplicationResponseModel } from 'src/app/models/applications.model';
import { genderOptions } from 'src/configs/dropdown-options/options.nl';
import { dateOfBirthFormatter } from 'src/app/utils/format-dateOfBirth';
import { genderValidator } from 'src/app/validators/gender';

@Component({
  selector: 'app-no-idin-bank',
  templateUrl: './no-idin-bank.component.html',
  styleUrls: ['./no-idin-bank.component.scss']
})
export class NoIdinBankComponent implements OnInit, OnDestroy {
    noIdinBankForm!: FormGroup;
    subs = Array<Subscription>();
    error: any = '';
    confirmationLoading: boolean = false;
    genderOptions = genderOptions;
    minAge!: Date;
    startDate!: Date;

    constructor(
        private formBuilder: FormBuilder,
        private translateService: TranslateService,
        private basicInfoService: BasicInfoService,
        private applicationService: ApplicationService,
        private trackingService: TrackingService,
        private flowService: FlowService,
        private loginApi: LoginApiService,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        this.noIdinBankForm = this.formBuilder.group({
            firstName: ['', [Validators.required, Validators.pattern('[A-Za-zÀ-ÿ ]*')]],
            lastNamePrefix: ['', [Validators.pattern('[A-Za-zÀ-ÿ ]*')]],
            lastName: ['', [Validators.required, Validators.pattern('[A-Za-zÀ-ÿ ]*')]],
            gender: [null, [Validators.required, genderValidator()]],
            dateOfBirth: [null, [Validators.required, dateOfBirthValidator()]],
            bsn: ['', [Validators.required, dutchPersonNumberValidator()]],
        });

        this.minAge = this.setDate(18);
        this.startDate = this.setDate(50);
    }

    setDate(inputYear: number): Date {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();
        return new Date(currentYear - inputYear, currentMonth, currentDay);
    }

    trackFields(): void {
        if (this.firstName.valid) {
            this.trackingService.filledField('firstName');
        }
        if (this.lastNamePrefix.valid) {
            this.trackingService.filledField('lastNamePrefix');
        }
        if (this.lastName.valid) {
            this.trackingService.filledField('lastName');
        }
        if (this.bsn.valid) {
            this.trackingService.filledField('bsn');
        }
        if (this.gender.valid) {
            this.trackingService.filledField('gender');
        }
        if (this.dateOfBirth.valid) {
            this.trackingService.filledField('dateOfBirth');
        }
    }

    handleButtonClick(): void {
        if (this.noIdinBankForm.invalid || this.confirmationLoading) return;
        this.confirmationLoading = true;
        this.trackFields();

        this.subs.push(
            this.loginApi.verifyWeak(this.basicInfoService.leadId, this.bsn.value)
                .pipe(
                    retry(2),
                    mergeMap((response: VerifyWeakResponse) => {
                        this.basicInfoService.personNumber = this.bsn.value;
                        this.authService.accessToken = response.token;
                        this.basicInfoService.agreedToTerms = true;
                        return this.basicInfoService.handlePostLogin(
                            this.firstName.value,
                            this.lastName.value, 
                            this.lastNamePrefix.value,
                            this.patchGender(this.gender),
                            this.formatDateOfBirth(this.dateOfBirth.value),
                            this.applicationService.estimatedTurnover,
                            this.applicationService.estimatedTurnoverPrev
                        );
                    })
                ).subscribe((response: AddApplicationResponseModel) => {
                    this.applicationService.populateServiceWithData(response);
                    this.applicationService.reason = this.basicInfoService.reason;
                    this.flowService.selectFlow(response.flowId)
                    this.flowService.goToInitialScreen();
                },
                (error: any) => {
                    this.handleApiError(error);
                    this.confirmationLoading = false;
                }
            )
        );
    }

  ngOnDestroy(): void {
      this.subs.map((sub) => sub.unsubscribe());
  }

  get form(): FormGroup {
    return this.noIdinBankForm;
  }

  get firstName(): FormControl {
    return this.form.get('firstName') as FormControl;
  }

  get lastNamePrefix(): FormControl {
    return this.form.get('lastNamePrefix') as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get('lastName') as FormControl;
  }

  get bsn(): FormControl {
    return this.form.get('bsn') as FormControl;
  }

  get gender(): FormControl {
    return this.form.get('gender') as FormControl;
  }

  get dateOfBirth(): FormControl {
    return this.form.get('dateOfBirth') as FormControl;
  }

  formatDateOfBirth(dateOfBirth: Date): string {
    return dateOfBirthFormatter(dateOfBirth);
  }

  patchGender(gender: FormControl): string {
      if (gender.value === 'neutral') {
          return 'male';
      }
      return gender.value;
  }

  handleApiError(error: any): void {
      if (error instanceof TimeoutError) {
          this.error = this.translateService.instant('identification.timeoutError');
      } else {
        this.error = `Oops! Something went wrong... ${error.message}`;
        //   this.error = JSON.stringify(error);
      }
      this.confirmationLoading = false;
  }

  getErrors(control: string): string | null {
    const firstName = this.firstName;
    const lastNamePrefix = this.lastNamePrefix;
    const lastName = this.lastName;
    const bsn = this.bsn;
    const gender = this.gender;
    const dateOfBirth = this.dateOfBirth;

    switch (control) {
      case 'firstName':
        if (firstName.hasError('required')) {
          return this.translateService.instant('global.error.firstNameRequired');
        } else if (firstName.hasError('pattern')) {
			return this.translateService.instant('global.error.firstNameInvalid');
        }
        return null;
      case 'lastNamePrefix':
        if (lastNamePrefix.hasError('pattern')) {
			return this.translateService.instant('global.error.lastNamePrefixInvalid');
        }
        return null;
      case 'lastName':
        if (lastName.hasError('required')) {
          return this.translateService.instant('global.error.lastNameRequired');
        } else if (lastName.hasError('pattern')) {
			return this.translateService.instant('global.error.lastNameInvalid');
        }
    return null;
      case 'bsn':
        if (bsn.hasError('required')) {
          return this.translateService.instant('global.error.personNumberRequired');
        } 
        else if (bsn.hasError('invalidDutchSSN')) {
          return this.translateService.instant('global.error.personNumberInvalid');
        }
        return null;
      case 'gender':
        if (gender.hasError('required')) {
            return this.translateService.instant('global.error.genderRequired');
        } else if (gender.hasError('genderInvalid')) {
            return this.translateService.instant('global.error.genderInvalid');
        }
        return null;
      case 'dateOfBirth':
        if (dateOfBirth.hasError('required')) {
            return this.translateService.instant('global.error.dateOfBirthRequired');
        } else if (dateOfBirth.hasError('invalidDateOfBirth')) {
            return this.translateService.instant('global.error.dateOfBirthInvalid');
        }
        return null;
      default:
        return null;
    }
  }
}
